<script lang="ts">
import { captureException } from '@sentry/browser';

import type { Cart } from '@/composables/shoppingcart';
import {
  addToDataLayer,
  enableTrackingFunction,
  enableTrackingShopFunction,
  getCartItemsFunction,
  getCategories,
  getPriceForTrackingFunction,
  isShopFunction,
  trackAddMultipleToCartFunction,
  trackAddToCartFunction,
  type TrackingEventItem,
} from '@/composables/tracking';
import type { GetBestPriceQuery, GetCatalogProductDataByIdQuery } from '@/generated/graphql';
import GET_CATALOG_PRODUCT_DATA_BY_ID from '@/graphql/getCatalogProductDataById.gql';
import eventHubMixin from '@/mixins/eventHubMixin.vue';

// @ts-ignore
const GET_BESTPRICE = () => import('@/graphql/getBestPrice.gql');

export default {
  mixins: [eventHubMixin],
  computed: {
    isShop(): boolean {
      return isShopFunction(this.$fischer);
    },
    brandName(): string {
      return this.$fischer.brandName();
    },
    enableTracking(): boolean {
      return enableTrackingFunction(this.$fischer);
    },
    enableTrackingShop(): boolean {
      return enableTrackingShopFunction(this.$fischer);
    },
  },
  methods: {
    async trackViewItem(data: any, isFamily: boolean): Promise<void> {
      try {
        if (!this.enableTracking) {
          return;
        }

        const ecomLang = isFamily
          ? data.TextList.find((el: any) => el.Key === 'BEZEICHNUNG')?.Value
          : data.TextList.find((el: any) => el.Key === 'ECOMLANG')?.Value;
        const itemId = isFamily ? data.CategoryId : data.ProductId;

        let item: TrackingEventItem = {
          item_name: ecomLang,
          item_id: itemId,
          item_brand: this.brandName,
          ...this.getCategories(data.CategoryDisplayNamePath),
        };

        if (!isFamily) {
          item.item_variant = data.ProductName;
        }

        const { price, quantity, currency, totalPrice } = await this.getPriceForTracking(
          data.ProductId,
        );
        if (price) {
          item.price = price;
          item.quantity = quantity;
        }

        let ecommerce;
        if (totalPrice) {
          ecommerce = {
            currency: currency,
            value: totalPrice,
          };
        }

        this.addToDataLayer('view_item', [item], ecommerce);
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    async trackAddProductToWishlist(product: any): Promise<void> {
      try {
        if (!(this.enableTracking && this.enableTrackingShop)) {
          return;
        }

        const ecomLang = product.TextList.find((el: any) => el.Key === 'ECOMLANG')?.Value;

        const item: TrackingEventItem = {
          item_name: ecomLang,
          item_id: product.ProductId,
          item_brand: this.brandName,
          item_variant: product.ProductName,
          ...this.getCategories(product.CategoryDisplayNamePath),
        };

        const { price, quantity, currency, totalPrice } = await this.getPriceForTracking(
          product.ProductId,
        );
        if (price) {
          item.price = price;
          item.quantity = quantity;
        }

        let ecommerce;
        if (totalPrice) {
          ecommerce = {
            currency: currency,
            value: totalPrice,
          };
        }

        this.addToDataLayer('add_to_wishlist', [item], ecommerce);
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    trackAddCartToWishlist(cart: any): void {
      try {
        if (!(this.enableTracking && this.enableTrackingShop)) {
          return;
        }

        const items: TrackingEventItem[] = this.getCartItems(cart);
        const ecommerce = {
          currency: cart.subTotal?.currencyIso,
          value: cart.subTotal?.value,
        };
        if (items.length) {
          this.addToDataLayer('add_to_wishlist', items, ecommerce);
        }
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    trackViewCart(cart: any): void {
      try {
        if (!(this.enableTracking && this.enableTrackingShop)) {
          return;
        }

        const items: TrackingEventItem[] = this.getCartItems(cart);

        const ecommerce = {
          currency: cart.subTotal?.currencyIso,
          value: cart.subTotal?.value,
        };

        if (items.length) {
          this.addToDataLayer('view_cart', items, ecommerce);
        }
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    async trackAddToCart(
      cartId: number | string,
      productIdUnpadded: string,
      qty: number,
      fromMemoList: boolean,
      actionType?: string,
    ): Promise<void> {
      return trackAddToCartFunction(
        cartId,
        productIdUnpadded,
        qty,
        fromMemoList,
        actionType,
        this.enableTracking,
        this.enableTrackingShop,
        (variables) =>
          this.$apollo.query<GetCatalogProductDataByIdQuery>({
            query: GET_CATALOG_PRODUCT_DATA_BY_ID,
            errorPolicy: 'all',
            variables: variables || undefined,
          }),
        this.$fischer,
        (...args) => this.getPriceForTracking(...args),
        (...args) => this.sendEvent?.(...args),
      );
    },
    async trackAddMultipleToCart(
      cartId: number | string,
      productsWithQuantity: {
        id: string;
        quantity: number;
      }[],
    ): Promise<void> {
      const query = (await GET_BESTPRICE()).default;
      return trackAddMultipleToCartFunction(
        cartId,
        productsWithQuantity,
        this.$fischer,
        (variables) =>
          this.$apollo.query<GetCatalogProductDataByIdQuery>({
            query,
            errorPolicy: 'all',
            variables,
          }),
        (type, data) => this.sendEvent(type, data),
      );
    },
    async trackRemoveFromCart(productList: any): Promise<void> {
      try {
        if (!(this.enableTracking && this.enableTrackingShop)) {
          return;
        }

        const items: TrackingEventItem[] = [];
        productList.forEach((product: any) => {
          let item: TrackingEventItem = {
            item_name: product.name,
            item_id: product.articleNo,
            item_brand: this.brandName,
            item_variant: product.catalogProductData?.ProductName,
            quantity: product.quantity,
            ...this.getCategories(product.catalogProductData?.CategoryDisplayNamePath),
          };

          if (product.priceValue && product.initialQuantity) {
            item.price = product.priceValue / product.initialQuantity;
          }

          items.push(item);
        });

        this.addToDataLayer('remove_from_cart', items);
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    trackBeginCheckout(cart: any): void {
      try {
        if (!(this.enableTracking && this.enableTrackingShop)) {
          return;
        }

        const items: TrackingEventItem[] = this.getCartItems(cart);
        const ecommerce = {
          currency: cart.subTotal?.currencyIso,
          value: cart.subTotal?.value,
        };

        if (items.length) {
          this.addToDataLayer('begin_checkout', items, ecommerce);
        }
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    trackOnlineRetailerClicked(cart: any, retailerName: string): void {
      try {
        if (!this.enableTracking && !this.enableTrackingShop) {
          return;
        }

        const items: TrackingEventItem[] = this.getCartItems(cart);
        const ecommerce = {
          retailerName,
          cartItems: items?.length,
        };

        this.addToDataLayer('RCClickedOnRetailer', items, ecommerce);
      } catch (error) {
        console.error(error);
        captureException(error);
      }
    },
    getCartItems(cart: Cart): TrackingEventItem[] {
      return getCartItemsFunction(cart, this.$fischer);
    },
    getCategories(paths: string[]): Record<string, string> {
      return getCategories(paths);
    },
    async getPriceForTracking(
      productIdUnpadded: string,
      qty: number = 0,
    ): Promise<{
      price: number | undefined;
      quantity: number;
      currency?: string;
      totalPrice: number | undefined;
    }> {
      const query = (await GET_BESTPRICE()).default;
      return getPriceForTrackingFunction(productIdUnpadded, qty, this.$fischer, (variables) =>
        this.$apollo.query<GetBestPriceQuery>({
          query,
          errorPolicy: 'all',
          variables,
        }),
      );
    },
    addToDataLayer(event: string, items: TrackingEventItem[], ecommerce?: object): void {
      addToDataLayer(event, items, ecommerce);
    },
  },
};
</script>
